<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from './helpers/use-locale';
import MdiFaIcon from './MdiFaIcon.vue';

type Emits = {
  (e: 'update:modelValue', isSelecting: boolean): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: boolean;
  rounded?: 'sm' | 'default' | 'lg';
  variant?: 'text' | 'elevated' | 'flat' | 'tonal' | 'outlined' | 'plain';
  color?: string;
  size?: 'x-small' | 'small' | 'default';
  bordered?: boolean;
  ripple?: boolean;
  selectIcon?: string;
  cancelIcon?: string;
};
const props = withDefaults(defineProps<Props>(), {
  rounded: 'lg',
  variant: 'flat',
  color: 'surface-container',
  size: undefined,
  bordered: true,
  ripple: true,
  selectIcon: 'mdi-check-circle-outline',
  cancelIcon: 'mdi-close-circle-outline',
});

const { t } = useLocale();
const { xs } = useDisplay();

const roundedClass = computed(() => (props.rounded === 'default' ? undefined : props.rounded));

const buttonSize = computed(() => props.size ?? (xs.value ? 'small' : 'default'));
</script>

<template>
  <div class="select-mode-button d-flex align-center">
    <v-btn
      :class="['text-none', { 'select-btn': bordered }]"
      :variant="variant"
      :color="color"
      :size="buttonSize"
      :rounded="roundedClass"
      :ripple="ripple"
      flat
      data-testid="toggle-select-btn"
      @click="emit('update:modelValue', !modelValue)"
    >
      <template v-if="!xs" #prepend>
        <MdiFaIcon :size="20" :icon="modelValue ? cancelIcon : selectIcon" />
      </template>
      {{ modelValue ? t('components.cancel') : t('components.select') }}
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
.select-mode-button {
  min-height: 36px;
}

.select-btn {
  border: 1px solid rgb(var(--v-theme-outline-variant));
}

.v-btn:hover {
  --v-hover-opacity: 0;
  --v-activated-opacity: 0;
}
</style>
